import React, { useCallback } from 'react';
import { renderPillRow, type PillRowProps } from '@/app/common/filters/surfaces/bar/pills/PillRow.tsx';
import type { OnDynamicFilterUpdate } from '@/app/common/filters/surfaces/bar/pills/FilterGroupPill.tsx';
import { applyUpdatedFilters } from '@/app/common/filters/core/filterUtils.ts';
import { updateDynamicFilters } from '@/app/common/filters/searchParams/updateDynamicFilters.ts';
import { isFilterThatCanFilterItself } from '@/app/common/filters/core/filterDefinitions.ts';
import { useSearchPageWrapperData } from '@/app/components/search/searchPageCommonWrapperProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';

type StickyFirstPillProps = Omit<PillRowProps, 'onDynamicFilterUpdate'>;

export const useDynamicFilterUpdate = (): OnDynamicFilterUpdate => {
  const { filtersGroups } = useSearchPageWrapperData();
  const { searchInfoData, commonData, curiosityParams, localization } = usePageWrapperData();
  const { productsData } = useSearchPageData();
  const { translationDictionary } = useTranslation();

  return useCallback(
    ({ updatedFilters, currentPillFilters, isCancelled, onComplete }) => {
      const rebuildGroups = isFilterThatCanFilterItself(updatedFilters);

      const prevFiltersGroups = filtersGroups.map((prevGroup) => {
        if (!('filters' in prevGroup)) return prevGroup;
        return {
          ...prevGroup,
          filters: prevGroup.filters.map((filter) => {
            const pillFilter = currentPillFilters.find((pf) => pf.name === filter.name);
            return pillFilter ?? filter;
          }),
        };
      });

      updateDynamicFilters({
        filtersGroups: applyUpdatedFilters({ prevFiltersGroups, updatedFilters }),
        updatedFilters,
        searchInfoData,
        productsData,
        commonData,
        localization,
        translationDictionary,
        rebuildGroups,
        isCancelled,
        handleFiltersGroupsUpdated: onComplete,
      });
    },
    [filtersGroups, searchInfoData, productsData, commonData, localization, curiosityParams, translationDictionary]
  );
};

export const StickyFirstPill = ({
  filterPills,
  customFilterMainCreators,
  customFilterLabelCreators,
  isLoading,
  disabled,
  handleFilterChanged,
  useModal,
}: StickyFirstPillProps) => {
  const onDynamicFilterUpdate = useDynamicFilterUpdate();
  return (
    <>
      {renderPillRow({
        filterPills,
        customFilterMainCreators,
        customFilterLabelCreators,
        isLoading,
        disabled,
        handleFilterChanged,
        useModal,
        onDynamicFilterUpdate,
      })}
    </>
  );
};
